import { Check, Info } from 'lucide-react'
import { CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { LocationPicker } from '@/components/ui/location-picker'
import { FileUploadField, type FileUploaderFn } from '@/components/registration/FileUploadField'
import { useLocalization } from '@/contexts/LocalizationContext'
import type { TransformedQuestion } from '@/utils/evaluationSnapshotTransformer'
import {
  isValidRegexPattern,
  getRegexFormatHint,
} from '@/utils/validation'

interface OrderQuestionRendererProps {
  /** The transformed question to render. */
  question: TransformedQuestion
  /** Current answer value (keyed by question id). */
  value: any
  /** Callback when the answer changes. Receives (questionId, newValue). */
  onChange: (questionId: string, value: any) => void
  /** Whether the current locale is Arabic. */
  isArabic: boolean
  /** Whether this question currently has a validation error. */
  hasError: boolean
  /** Whether this question has been answered. */
  isAnswered: boolean
  /**
   * Global 1-based question number for the circle indicator.
   * If omitted, the circle indicator is hidden.
   */
  questionNumber?: number
  /**
   * Whether to show the question header (number, text, required marker).
   * Defaults to true. Set to false to show only the input.
   */
  showQuestionHeader?: boolean
  /** Custom file upload function for evaluation-context uploads */
  uploadFn?: FileUploaderFn
  /**
   * Specific validation error message (e.g. regex mismatch).
   * When set, displayed instead of the generic "required" message.
   */
  errorMessage?: string
}

/**
 * Renders a single survey question with its header and input control.
 *
 * Extracted from QuestionsPage to be reusable across the order evaluation flow.
 * Preserves the exact same visual styling as the original inline rendering.
 */
export function OrderQuestionRenderer({
  question,
  value,
  onChange,
  isArabic,
  hasError: hasErr,
  isAnswered: answered,
  questionNumber,
  showQuestionHeader = true,
  uploadFn,
  errorMessage,
}: OrderQuestionRendererProps) {
  const { t } = useLocalization()

  return (
    <div>
      {showQuestionHeader && (
        <CardHeader className="pb-3 px-0 pt-0">
          <div className="flex items-start gap-3">
            {/* Question number indicator */}
            {questionNumber != null && (
              <div
                className={cn(
                  'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
                  answered
                    ? 'bg-primary text-primary-foreground'
                    : hasErr
                      ? 'bg-destructive/10 text-destructive border border-destructive'
                      : 'bg-muted text-muted-foreground',
                )}
              >
                {answered ? <Check className="h-4 w-4" /> : questionNumber}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <CardTitle className="text-base font-medium leading-relaxed">
                {isArabic
                  ? question.text_translations.ar
                  : question.text_translations.en}
                {question.validation_rules.required && (
                  <span className="text-destructive mx-1">*</span>
                )}
              </CardTitle>
              {hasErr && (
                <p className="text-xs text-destructive mt-1">
                  {errorMessage || t('orderCreation.questionRequired')}
                </p>
              )}
            </div>
          </div>
        </CardHeader>
      )}

      <CardContent className="pt-0 px-0">
        <QuestionInput
          question={question}
          value={value}
          onChange={onChange}
          isArabic={isArabic}
          hasError={hasErr}
          uploadFn={uploadFn}
        />
      </CardContent>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helper: whether a yes_no question should show file upload when user picks Yes
// (e.g. "Attach wound photo/report if available")
// ---------------------------------------------------------------------------

function shouldShowFileWhenYes(question: TransformedQuestion): boolean {
  const validation = question.validation_rules as Record<string, unknown> | undefined
  if (validation?.show_file_when_yes === true) return true
  const code = (question.code || '').toLowerCase()
  const textEn = (question.text_translations?.en || '').toLowerCase()
  const textAr = question.text_translations?.ar || ''
  const attachmentKeywords = [
    'attach',
    'attachment',
    'upload',
    'photo',
    'report',
    'file',
    'wound',
    'مرفق',
    'ملف',
    'تقرير',
    'صورة',
    'رفع',
  ]
  const combined = `${code} ${textEn} ${textAr}`.toLowerCase()
  return attachmentKeywords.some((kw) => combined.includes(kw.toLowerCase()))
}

// ---------------------------------------------------------------------------
// Internal: renders the input control based on question type
// ---------------------------------------------------------------------------

function QuestionInput({
  question,
  value,
  onChange,
  isArabic,
  hasError: hasErr,
  uploadFn,
}: {
  question: TransformedQuestion
  value: any
  onChange: (questionId: string, value: any) => void
  isArabic: boolean
  hasError: boolean
  uploadFn?: FileUploaderFn
}) {
  const { t } = useLocalization()
  const hasOptions = question.options && question.options.length > 0
  const regexPattern = question.validation_rules.pattern
  const formatHint = isValidRegexPattern(regexPattern)
    ? getRegexFormatHint(regexPattern, isArabic)
    : null

  const inputClasses = cn(
    'w-full border rounded-lg px-4 py-3 text-base transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
    hasErr && 'border-destructive focus:ring-destructive/20',
  )

  switch (question.question_type) {
    case 'text':
      return (
        <div>
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(question.id, e.target.value)}
            className={inputClasses}
            placeholder={t('orderCreation.enterAnswerHere')}
          />
          {formatHint && !hasErr && (
            <p className="text-xs text-muted-foreground mt-1">{formatHint}</p>
          )}
        </div>
      )

    case 'long_text':
      return (
        <div>
          <textarea
            value={value || ''}
            onChange={(e) => onChange(question.id, e.target.value)}
            rows={4}
            className={cn(inputClasses, 'resize-none')}
            placeholder={t('orderCreation.enterAnswerHere')}
          />
          {formatHint && !hasErr && (
            <p className="text-xs text-muted-foreground mt-1">{formatHint}</p>
          )}
        </div>
      )
      case 'rating':
        return (
          <div className="space-y-3">
            {/* Labels */}
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0</span>
              <span>10</span>
            </div>
      
            {/* Rating buttons */}
            <div className="grid grid-cols-6 sm:grid-cols-11 gap-2">
              {Array.from({ length: 11 }, (_, value) => {
                const isSelected = Number(value) === value
      
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onChange(question.id, value)}
                    className={`
                      h-10 rounded-md border text-sm font-medium transition-all
                      flex items-center justify-center
                      ${
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground shadow-md scale-105'
                          : 'border-input bg-background hover:bg-accent hover:scale-105'
                      }
                    `}
                  >
                    {value}
                  </button>
                )
              })}
            </div>
      
            {/* Selected value */}
            {value !== undefined && (
              <div className="text-center text-sm font-semibold text-primary">
                Selected: {value}
              </div>
            )}
          </div>
        )
    case 'single_choice':
    case 'radio':
    case 'dropdown':
      if (!hasOptions) {
        return (
          <FallbackTextInput
            value={value}
            onChange={(v) => onChange(question.id, v)}
            className={inputClasses}
            placeholder={t('orderCreation.enterAnswerHere')}
            hint={t('orderCreation.enterAnswerInField')}
          />
        )
      }
      return (
        <div className="space-y-2">
          {question.options!.map((option) => (
            <div
              key={option.id}
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onChange(question.id, option.value)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onChange(question.id, option.value)
                }
              }}
              className={cn(
                'flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all select-none',
                'hover:bg-accent/50 hover:border-primary/30',
                value === option.value &&
                  'bg-primary/5 border-primary ring-1 ring-primary/20',
              )}
            >
              <div
                className={cn(
                  'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors',
                  value === option.value
                    ? 'border-primary bg-primary'
                    : 'border-muted-foreground/30',
                )}
              >
                {value === option.value && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
              <span className="flex-1">
                {isArabic
                  ? option.label_translations.ar
                  : option.label_translations.en}
              </span>
            </div>
          ))}
        </div>
      )

    case 'multiple_choice':
    case 'checkbox':
      if (!hasOptions) {
        return (
          <FallbackTextInput
            value={value}
            onChange={(v) => onChange(question.id, v)}
            className={inputClasses}
            placeholder={t('orderCreation.enterAnswerHere')}
            hint={t('orderCreation.enterAnswerInField')}
          />
        )
      }
      return (
        <div className="space-y-2">
          {question.options!.map((option) => {
            const isChecked =
              Array.isArray(value) && value.includes(option.value)
            return (
              <div
                key={option.id}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const current = Array.isArray(value) ? value : []
                  const newValue = isChecked
                    ? current.filter((v: string) => v !== option.value)
                    : [...current, option.value]
                  onChange(question.id, newValue)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    const current = Array.isArray(value) ? value : []
                    const newValue = isChecked
                      ? current.filter((v: string) => v !== option.value)
                      : [...current, option.value]
                    onChange(question.id, newValue)
                  }
                }}
                className={cn(
                  'flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all select-none',
                  'hover:bg-accent/50 hover:border-primary/30',
                  isChecked &&
                    'bg-primary/5 border-primary ring-1 ring-primary/20',
                )}
              >
                <div
                  className={cn(
                    'w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
                    isChecked
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground/30',
                  )}
                >
                  {isChecked && <Check className="h-3 w-3 text-white" />}
                </div>
                <span className="flex-1">
                  {isArabic
                    ? option.label_translations.ar
                    : option.label_translations.en}
                </span>
              </div>
            )
          })}
        </div>
      )

    case 'number':
      return (
        <div>
          <input
            type="number"
            value={value || ''}
            onChange={(e) =>
              onChange(
                question.id,
                e.target.value ? parseFloat(e.target.value) : '',
              )
            }
            className={inputClasses}
            placeholder={t('orderCreation.enterNumber')}
          />
          {formatHint && !hasErr && (
            <p className="text-xs text-muted-foreground mt-1">{formatHint}</p>
          )}
        </div>
      )

    case 'yes_no': {
      const isYesSelected =
        value === 'yes' ||
        value === true ||
        (value && typeof value === 'object' && value.choice === 'yes')
      const isNoSelected = value === 'no' || value === false
      const showFileWhenYes = shouldShowFileWhenYes(question)
      const fileValue =
        value && typeof value === 'object' && value.choice === 'yes'
          ? value.fileId ?? value.fileIds
          : undefined

      return (
        <div className="space-y-4">
          <div className="flex gap-4">
            {[
              {
                val: 'yes',
                labelEn: 'Yes',
                labelAr: 'نعم',
                isSelected: isYesSelected,
              },
              {
                val: 'no',
                labelEn: 'No',
                labelAr: 'لا',
                isSelected: isNoSelected,
              },
            ].map((opt) => (
              <div
                key={opt.val}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (opt.val === 'no') {
                    onChange(question.id, 'no')
                  } else {
                    const keepFile =
                      isYesSelected &&
                      value &&
                      typeof value === 'object' &&
                      value.fileId
                    onChange(
                      question.id,
                      keepFile ? { choice: 'yes', fileId: value.fileId } : 'yes',
                    )
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    if (opt.val === 'no') {
                      onChange(question.id, 'no')
                    } else {
                      const keepFile =
                        isYesSelected &&
                        value &&
                        typeof value === 'object' &&
                        value.fileId
                      onChange(
                        question.id,
                        keepFile
                          ? { choice: 'yes', fileId: value.fileId }
                          : 'yes',
                      )
                    }
                  }
                }}
                className={cn(
                  'flex-1 flex items-center justify-center gap-2 p-4 border rounded-lg cursor-pointer transition-all select-none',
                  'hover:bg-accent/50 hover:border-primary/30',
                  opt.isSelected &&
                    'bg-primary/5 border-primary ring-1 ring-primary/20',
                )}
              >
                <div
                  className={cn(
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors',
                    opt.isSelected
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground/30',
                  )}
                >
                  {opt.isSelected && (
                    <div className="w-2 h-2 rounded-full bg-white" />
                  )}
                </div>
                <span className="font-medium">
                  {isArabic ? opt.labelAr : opt.labelEn}
                </span>
              </div>
            ))}
          </div>

          {showFileWhenYes && isYesSelected && (
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <p className="text-sm font-medium text-muted-foreground mb-3">
                {t('orderCreation.attachFileBelow')}
              </p>
              <FileUploadField
                questionCode={question.code}
                multiple={false}
                accept="image/*,.pdf,.doc,.docx"
                maxSizeMB={10}
                value={fileValue}
                onChange={(fileIds) => {
                  const id = Array.isArray(fileIds) ? fileIds[0] : fileIds
                  onChange(question.id, { choice: 'yes', fileId: id || undefined })
                }}
                locale={isArabic ? 'ar' : 'en'}
                disabled={false}
                uploadFn={uploadFn}
              />
            </div>
          )}
        </div>
      )
    }

    case 'yes_no_conditional': {
      const isYesSelected =
        value === 'yes' ||
        value === true ||
        (value && typeof value === 'object' && value.choice === 'yes')
      const isNoSelected = value === 'no' || value === false
      const currentDetails =
        value && typeof value === 'object' ? (value.details ?? '') : ''

      return (
        <div className="space-y-4">
          <div className="flex gap-4">
            {[
              {
                val: 'yes',
                labelEn: 'Yes',
                labelAr: 'نعم',
                isSelected: isYesSelected,
              },
              {
                val: 'no',
                labelEn: 'No',
                labelAr: 'لا',
                isSelected: isNoSelected,
              },
            ].map((opt) => (
              <div
                key={opt.val}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (opt.val === 'no') {
                    onChange(question.id, 'no')
                  } else {
                    onChange(
                      question.id,
                      currentDetails
                        ? { choice: 'yes', details: currentDetails }
                        : 'yes',
                    )
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    if (opt.val === 'no') {
                      onChange(question.id, 'no')
                    } else {
                      onChange(
                        question.id,
                        currentDetails
                          ? { choice: 'yes', details: currentDetails }
                          : 'yes',
                      )
                    }
                  }
                }}
                className={cn(
                  'flex-1 flex items-center justify-center gap-2 p-4 border rounded-lg cursor-pointer transition-all select-none',
                  'hover:bg-accent/50 hover:border-primary/30',
                  opt.isSelected &&
                    'bg-primary/5 border-primary ring-1 ring-primary/20',
                )}
              >
                <div
                  className={cn(
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors',
                    opt.isSelected
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground/30',
                  )}
                >
                  {opt.isSelected && (
                    <div className="w-2 h-2 rounded-full bg-white" />
                  )}
                </div>
                <span className="font-medium">
                  {isArabic ? opt.labelAr : opt.labelEn}
                </span>
              </div>
            ))}
          </div>

          {isYesSelected && (
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <p className="text-sm font-medium text-muted-foreground mb-3">
                {isArabic ? 'يرجى وصف التفاصيل' : 'Please describe the details'}
              </p>
              <textarea
                rows={3}
                value={currentDetails}
                onChange={(e) =>
                  onChange(question.id, {
                    choice: 'yes',
                    details: e.target.value,
                  })
                }
                className={cn(inputClasses, 'resize-none')}
                placeholder={
                  isArabic ? 'اكتب التفاصيل هنا...' : 'Write details here...'
                }
              />
            </div>
          )}
        </div>
      )
    }

    case 'location':
      return (
        <LocationPicker
          value={value}
          onChange={(locationValue) => onChange(question.id, locationValue)}
          placeholder="Search for a location..."
          placeholderAr="ابحث عن موقع..."
          isArabic={isArabic}
          hasError={hasErr}
        />
      )

    case 'file_upload':
    case 'file': {
      const validation = question.validation_rules as Record<string, unknown> | undefined
      const fileTypes = validation?.file_types as string[] | undefined
      const maxFiles = (validation?.max_files as number) ?? 1
      const maxSizeMB = (validation?.max_size_mb as number) ?? 10
      const accept = fileTypes?.length
        ? fileTypes.join(',')
        : 'image/*,.pdf,.doc,.docx'
      return (
        <FileUploadField
          questionCode={question.code}
          multiple={maxFiles > 1}
          accept={accept}
          maxSizeMB={maxSizeMB}
          value={value}
          onChange={(fileIds) => onChange(question.id, fileIds)}
          locale={isArabic ? 'ar' : 'en'}
          disabled={false}
          uploadFn={uploadFn}
        />
      )
    }

    case 'date':
      return (
        <input
          type="date"
          value={value || ''}
          onChange={(e) => onChange(question.id, e.target.value)}
          className={inputClasses}
        />
      )

    case 'time':
      return (
        <input
          type="time"
          value={value || ''}
          onChange={(e) => onChange(question.id, e.target.value)}
          className={inputClasses}
        />
      )

    default:
      // Default to text input for unknown types
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(question.id, e.target.value)}
          className={inputClasses}
          placeholder={t('orderCreation.enterAnswerHere')}
        />
      )
  }
}

// ---------------------------------------------------------------------------
// Fallback text input for choice questions with no options
// ---------------------------------------------------------------------------

function FallbackTextInput({
  value,
  onChange,
  className,
  placeholder,
  hint,
}: {
  value: any
  onChange: (value: string) => void
  className: string
  placeholder: string
  hint: string
}) {
  return (
    <div className="space-y-3">
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className={className}
        placeholder={placeholder}
      />
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <Info className="h-3 w-3" />
        {hint}
      </p>
    </div>
  )
}
